import React from 'react';
import { Trash2, GripVertical } from 'lucide-react';
import { cn } from '../utils/cn';
import type { Question, QuestionOption } from '../types';

interface QuestionEditorProps {
  question: Question;
  onChange: (field: keyof Question, value: any) => void;
  onOptionChange: (index: number, field: keyof QuestionOption, value: any) => void;
  onDeleteOption: (index: number) => void;
  onAddOption: () => void;
}

export function QuestionEditor({
  question,
  onChange,
  onOptionChange,
  onDeleteOption,
  onAddOption
}: QuestionEditorProps) {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Texto da Questão
        </label>
        <input
          type="text"
          value={question.text || ''}
          onChange={(e) => onChange('text', e.target.value)}
          className={cn(
            "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2",
            "focus:border-purple-500 focus:ring-purple-500"
          )}
          placeholder="Digite a questão..."
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Tipo de Questão
          </label>
          <select
            value={question.type || 'single'}
            onChange={(e) => onChange('type', e.target.value)}
            className={cn(
              "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2",
              "focus:border-purple-500 focus:ring-purple-500"
            )}
          >
            <option value="single">Única Escolha</option>
            <option value="multiple">Múltipla Escolha</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Pontos
          </label>
          <input
            type="number"
            min="1"
            value={question.points || 1}
            onChange={(e) => onChange('points', parseInt(e.target.value, 10))}
            className={cn(
              "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2",
              "focus:border-purple-500 focus:ring-purple-500"
            )}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Explicação (Opcional)
        </label>
        <textarea
          value={question.explanation || ''}
          onChange={(e) => onChange('explanation', e.target.value)}
          className={cn(
            "mt-1 block w-full rounded-md border border-gray-300 px-3 py-2",
            "focus:border-purple-500 focus:ring-purple-500"
          )}
          rows={2}
          placeholder="Explique a resposta correta..."
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Opções de Resposta
          </label>
          <button
            onClick={onAddOption}
            disabled={question.options.length >= 6}
            className={cn(
              "text-sm text-purple-600 hover:text-purple-700",
              "disabled:opacity-50 disabled:hover:text-purple-600"
            )}
          >
            Adicionar Opção
          </button>
        </div>

        {question.options.map((option, index) => (
          <div key={option.id} className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex-shrink-0">
                <GripVertical className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type={question.type === 'single' ? 'radio' : 'checkbox'}
                checked={option.isCorrect || false}
                onChange={(e) => onOptionChange(index, 'isCorrect', e.target.checked)}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500"
              />
              <input
                type="text"
                value={option.text || ''}
                onChange={(e) => onOptionChange(index, 'text', e.target.value)}
                placeholder={`Opção ${index + 1}`}
                className={cn(
                  "block w-full rounded-md border border-gray-300 px-3 py-2",
                  "focus:border-purple-500 focus:ring-purple-500"
                )}
              />
              {question.options.length > 2 && (
                <button
                  onClick={() => onDeleteOption(index)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="ml-11">
              <input
                type="text"
                value={option.feedback || ''}
                onChange={(e) => onOptionChange(index, 'feedback', e.target.value)}
                placeholder="Feedback para esta opção (opcional)"
                className={cn(
                  "block w-full rounded-md border border-gray-300 px-3 py-2 text-sm",
                  "focus:border-purple-500 focus:ring-purple-500"
                )}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="required"
          checked={question.required || false}
          onChange={(e) => onChange('required', e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
        />
        <label htmlFor="required" className="text-sm font-medium text-gray-700">
          Questão obrigatória
        </label>
      </div>
    </div>
  );
}